import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../app/store';
import { Building, VerticalityFormInputs } from '../../types';
import Form from './Form';
import { handleBuildingChange, handleFloorChange, handleStairChange } from './helpers/dataHandlers';
import TitleSection from './TitleSection';
import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';
import { VerticalityFormSkelton } from './skeltons';

const VerticalityForm: React.FC = () => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const [buildingsList, setBuildingsList] = useState<string[]>([]);
  const [stairsList, setStairsList] = useState<string[]>([]);
  const [floorsList, setFloorsList] = useState<string[]>([]);
  const [outletsList, setOutletsList] = useState<string[]>([]);
  const { site, isSiteLoading } = useSelector((state: RootState) => state.site);

  const { watch, control, setValue, getValues, formState, trigger } =
    useForm<VerticalityFormInputs>({
      mode: 'onChange'
    });
  const { building, stair, floor } = getValues();
  const verticalityFormValues = watch();

  const selectAll = t('OrderList.all');

  useEffect(() => {
    if (site) {
      const buildingNames = site.buildings.map((v: Building) => v.name);
      setBuildingsList(buildingNames);

      const buildingToSet = site.buildings.length === 1 ? site.buildings[0].name : selectAll;

      setValue('building', buildingToSet);
    }
  }, [site]);

  useEffect(
    () =>
      handleBuildingChange(
        building,
        site?.buildings ?? [],
        setStairsList,
        setFloorsList,
        setOutletsList,
        setValue
      ),
    [building, site]
  );

  useEffect(
    () => handleStairChange(stair, building, site?.buildings ?? [], setFloorsList, setValue),
    [stair, building, site]
  );

  useEffect(
    () =>
      handleFloorChange(floor, building, stair, site?.buildings ?? [], setOutletsList, setValue),
    [floor, building, stair, site]
  );

  if (isSiteLoading) {
    return <VerticalityFormSkelton />;
  }

  if (!isSiteLoading && site !== undefined) {
    return (
      <>
        <TitleSection>
          {t('verticalityForm.specifyVerticality', `Specify the verticality`)}
        </TitleSection>
        <Form
          trigger={trigger}
          control={control}
          formList={{
            buildings: buildingsList,
            stairs: stairsList,
            floors: floorsList,
            outlets: outletsList
          }}
          formValues={verticalityFormValues}
          formState={formState}
        />
      </>
    );
  }
  return null;
};

export default VerticalityForm;
